import { Sparkles } from 'lucide-react';

interface SmartPasteMarkProps {
  /** Tile size. 'sm' for inline headers, 'md' for the entry-point card. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The animated AI mark, always in motion.
 *
 * Three layers on deliberately mismatched periods — a swelling halo (2.6s), the
 * drifting glyph itself (3.4s), and two satellite sparks blinking on their own
 * offset beat (2.2s) — so the loop never reads as a single scale pulse. The
 * transforms are rotation/scale/opacity only, which look identical mirrored, so
 * nothing needs an RTL variant.
 */
const SmartPasteMark = ({ size = 'md', className = '' }: SmartPasteMarkProps) => {
  const tile = size === 'md' ? 'h-11 w-11 rounded-xl' : 'h-10 w-10 rounded-full';
  const glyph = size === 'md' ? 'h-5 w-5' : 'h-5 w-5';

  return (
    <span className={`relative grid shrink-0 place-items-center ${tile} bg-sd-primary/20 ${className}`}>
      {/* Halo — swells behind the glyph. */}
      <span
        aria-hidden
        className={`absolute inset-0 ${tile.split(' ').pop()} bg-sd-primary/50 blur-[2px] animate-sp-halo`}
      />
      {/* Satellite sparks — tiny, offset, blinking against the glyph's rhythm. */}
      <span
        aria-hidden
        className='absolute top-1 end-1 h-1 w-1 rounded-full bg-sd-foreground/70 animate-sp-spark-1'
      />
      <span
        aria-hidden
        className='absolute bottom-1.5 start-1.5 h-[3px] w-[3px] rounded-full bg-sd-foreground/50 animate-sp-spark-2'
      />
      <Sparkles
        className={`relative ${glyph} text-sd-foreground animate-sp-glyph`}
        strokeWidth={1.75}
      />
    </span>
  );
};

export default SmartPasteMark;
